import { setCookie } from '@/lib/cookies'
import { queryClient } from '@/main'
import { useAppStore } from './app-store'
import type { Locale } from 'shared/types'
import { isLocale } from 'shared/locales'
import { i18n, setupI18n, type I18n, type Messages } from '@lingui/core'
import { useLingui } from '@lingui/react'

export function useTranslation() {
  const { i18n: activeI18n } = useLingui()
  const setStore = useAppStore((state) => state.setStore)

  const language: Locale = isLocale(activeI18n.locale) ? activeI18n.locale : 'es-ES'

  const changeLanguage = (newLocale: string) => {
    loadTranslation(newLocale as Locale)
    setStore({ locale: newLocale as Locale })
    setCookie('language', newLocale)
    queryClient.invalidateQueries()
  }

  return {
    language,
    changeLanguage,
    languages: {
      'es-ES': 'Español',
      'ca-ES': 'Català',
      'en-US': 'English',
      'pt-BR': 'Português',
    },
  }
}

/**
 * Create the Lingui instance for the current render.
 *
 * On the server the app runs in a long-lived worker that serves many concurrent requests, so a single
 * shared `i18n` catalog is mutable global state: interleaved async SSR renders clobber each other's
 * active locale. A request resolved to `es-ES` emits `<html lang="es-ES">` while a concurrent request
 * flips the shared catalog to another locale, so the body renders in the wrong language; the client then
 * hydrates from `document.documentElement.lang` ("es-ES") and re-renders different text, throwing a
 * hydration mismatch (React #418 — seen almost only from crawlers hitting mixed locales in bursts).
 * Giving each server request its own instance means its catalog can't be touched by another request.
 *
 * On the client a single shared instance is correct (single-threaded per session) and keeps the
 * module-level `i18n` import valid for code that still reads it directly.
 */
export function createLocaleI18n(): I18n {
  return typeof document === 'undefined' ? setupI18n() : i18n
}

// Load + activate a locale's catalog. Always keeps the shared global `i18n` in sync (the client and code
// that reads the singleton directly depend on it); when a request-scoped `extra` instance is passed, it
// is activated too so the SSR render tree can use an instance no concurrent request can clobber. See
// {@link createLocaleI18n}.
export async function loadTranslation(locale: Locale, extra?: I18n): Promise<I18n> {
  const { messages } = (await import(`../locales/${locale}.po`)) as { messages: Messages }
  i18n.loadAndActivate({ locale, messages })
  if (extra && extra !== i18n) extra.loadAndActivate({ locale, messages })
  return extra ?? i18n
}
